import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { resolveStudentIdentity } from "@/lib/profile";
import {useAiCoach} from "@/hooks/useAiCoach";
import { assistantAvatar } from "@/mocks/aiCoach";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import { getDataClient } from "@/lib/dataClient";

export default function Topbar() {
  const { openCoach } = useAiCoach();
  const { user } = useAuth();
  const identity = resolveStudentIdentity(user);
  const roleLabel = identity.grade ? `${identity.grade} Öğrencisi` : "Öğrenci";
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const navigate = useNavigate();
  const [search,setSearch]=useState("");
  const [identityOpen, setIdentityOpen] = useState(false);
  const identityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!identityOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (identityRef.current && !identityRef.current.contains(e.target as Node)) {
        setIdentityOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [identityOpen]);

  const handleLogout = async () => {
    setIdentityOpen(false);
    const dataClient = getDataClient();
    await dataClient.auth.signOut();
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-20 bg-background-50 border-b border-background-200">
      <div className="flex items-center gap-3 h-16 px-4 md:px-6">
        {/* Search */}
        <form onSubmit={e=>{e.preventDefault();if(search.trim())navigate(`/ogrenci/arama?q=${encodeURIComponent(search.trim())}`);}} className="flex-1 max-w-md relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
          <input
            type="text"
            value={search} onChange={e=>setSearch(e.target.value)} maxLength={120} aria-label="Konu, ders veya soru ara" placeholder="Konu, ders veya soru ara..."
            className="w-full pl-9 pr-3 py-2 rounded-md border border-background-200 bg-background-100 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </form>

        <div className="ml-auto flex items-center gap-1.5">
          {/* AI Koç */}
          <button
            type="button"
            onClick={() => openCoach()}
            className="hidden md:flex items-center gap-2 whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors"
          >
            <img src={assistantAvatar} alt={`${name} Asistan`} className="w-6 h-6 rounded-full object-cover" />
            <span className="hidden lg:inline">{name} Asistan</span>
          </button>

          {/* Notifications */}
          <button
            type="button"
            className="relative w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors"
            onClick={()=>navigate("/ogrenci/bildirimler")} aria-label="Bildirimler"
          >
            <i className="ri-notification-3-line text-lg"></i>
          </button>

          {/* Messages */}
          <button
            type="button"
            className="relative w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors"
            onClick={()=>navigate("/ogrenci/mesajlar")} aria-label="Mesajlar"
          >
            <i className="ri-chat-3-line text-lg"></i>
          </button>

          {/* User */}
          <div className="relative" ref={identityRef}>
            <button
              type="button"
              onClick={() => setIdentityOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={identityOpen}
              className="hidden sm:flex items-center gap-2.5 pl-2 ml-1 border-l border-background-200 cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-semibold text-sm">
                {identity.initials}
              </span>
              <div className="leading-tight hidden md:block text-left">
                <p className="text-sm font-semibold text-foreground-950">{identity.name}</p>
                <p className="text-xs text-foreground-500">{roleLabel}</p>
              </div>
              <i
                className={`hidden md:inline ri-arrow-down-s-line text-base text-foreground-400 transition-transform ${
                  identityOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {identityOpen && (
              <div
                role="menu"
                aria-label="Hesap menüsü"
                className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-background-200 bg-background-50 p-1.5 z-50 animate-fade-down"
              >
                <div className="px-3 py-2 mb-1 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{identity.name}</p>
                  <p className="text-xs text-foreground-500 truncate">{roleLabel}</p>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex items-center justify-between gap-3 w-full rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="whitespace-nowrap">Çıkış Yap</span>
                  <i className="ri-logout-box-r-line text-foreground-400" aria-hidden="true"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
